import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import map from 'lodash/map';
import { waitForIsOptedOutOfTargeting } from '../../../../js/utils/targetingOptedOut';
import logger from '../../../logging/logger';
import adsSettings from '../../../utils/adsSettings';
import { State, getState, updateState } from '../../../utils/applicationState';
import parseUtils from '../../../utils/parseUtils';
import adSlotManager from '../../adSlotManager';
import { NO_FILL_LABEL } from '../../constants';
import Events from '../../media/Events';
import mediaEvents from '../../media/mediaEvents';
import reporter from '../../reporter';
export const onImpression = (e) => {
    const slot = adSlotManager.getSlotWhere([
        'elementId',
        e.slot.getSlotElementId(),
    ]);
    if (e.isEmpty) {
        mediaEvents.event(Events.Media.display, Events.Status.failed).emit({
            slot,
            errorCode: '',
            errorMessage: NO_FILL_LABEL,
            debugDescription: 'Display Ad, Non Companion',
        });
        reporter.fail(slot, NO_FILL_LABEL);
        return;
    }
    if (slot) {
        slot.currentDimensions = map(e.size, (s) => {
            return parseUtils.parseSize(s);
        });
        slot.creativeId = e.creativeId || '';
        mediaEvents.event(Events.Media.display, Events.Status.loaded).emit({
            slot,
            isCompanionAd: false,
        });
        reporter.impression(slot);
        updateState(State.videoAdPlayed, false);
    }
};
export function onComplete(slotData) {
    const refreshSlot = () => {
        const options = getState(State.options);
        const isSlotVisibilityExperimentEnabled = parseUtils.parseBool(get(options, 'ads.pauseRefresh.enabled'));
        if (isSlotVisibilityExperimentEnabled) {
            this.slotRefreshTriggered = true;
            this.releaseSlot(slotData.mintSlot);
            slotData.mintSlot.refresh();
        }
        else {
            const slotDataArray = this.slotDataArray || [];
            forEach(slotDataArray, (_slotData) => {
                this.releaseSlot(_slotData.mintSlot);
                _slotData.mintSlot.refresh();
            });
        }
    };
    if (!this.slotRefreshTriggered) {
        if (getState(State.tabFocus)) {
            refreshSlot();
        }
        else if (!slotData.onTabFocus) {
            slotData.onTabFocus = () => {
                refreshSlot();
            };
        }
    }
}
export function onTabFocusChange(isTabFocus) {
    if (isTabFocus) {
        forEach(this.slotDataArray, (slotData) => {
            if (slotData.onTabFocus) {
                slotData.onTabFocus();
            }
        });
    }
}
export function onCanShowAdsChange() {
    if (!adsSettings.canShowAds() && this.canShowAds) {
        forEach(this.slotDataArray, (slot) => {
            this.clearSlot(slot.mintSlot);
            this.releaseSlot(slot.mintSlot);
        });
    }
    this.canShowAds = adsSettings.canShowAds();
}
export const onResponse = (e) => {
    const slot = adSlotManager.getSlotWhere([
        'elementId',
        e.slot.getSlotElementId(),
    ]);
    logger.info({
        message: 'display.ad.handlers.on.response.debug.dimensions.issue',
        data: {
            slot: {
                name: slot.name,
                currentDimensions: slot.currentDimensions,
                formatDefinition: {
                    dimensions: slot.formatDefinition?.dimensions,
                },
                formats: slot.formats?.map((item) => ({
                    dimensions: item.dimensions,
                })),
            },
        },
    });
    if (slot) {
        mediaEvents.event(Events.Media.display, Events.Status.response).emit({
            slot,
            isCompanionAd: false,
        });
    }
};
export const onView = (e) => {
    const slot = adSlotManager.getSlotWhere([
        'elementId',
        e.slot.getSlotElementId(),
    ]);
    if (slot) {
        mediaEvents.event(Events.Media.display, Events.Status.viewable).emit({
            slot,
            isCompanionAd: false,
            isViewable: global.document.hasFocus() && slot.isAdSlotVisible(),
        });
        reporter.view(slot);
    }
};
export const showOrRefreshAds = function (amazonBidderEnabled, slotData, slot) {
    googletag.cmd.push(() => {
        const options = getState(State.options);
        const disabledAdParamsMap = get(options, 'ads.display.params.disabled.map');
        const ppid = getState(State.ppid);
        const canSetPpid = !get(disabledAdParamsMap, [
            'ppid',
            slot.formatDefinition.adUnitId,
        ]);
        googletag.pubads().setPublisherProvidedId(canSetPpid ? ppid : '');
        if (amazonBidderEnabled) {
            apstag.setDisplayBids();
        }
        if (slotData.canRefresh) {
            googletag.pubads().refresh([slotData.dfpSlot]);
        }
        else {
            this.clearSlot(slot);
            googletag.display(slot.elementId);
            slotData.canRefresh = true;
        }
    });
};
export async function setPubAdsConsentStatus() {
    const isUserInUS = getState(State.isUserInUS);
    if (isUserInUS) {
        return;
    }
    await waitForIsOptedOutOfTargeting();
    const canShowPersonalizedAds = getState(State.canShowPersonalizedAds);
    googletag.cmd.push(() => {
        googletag.pubads().setPrivacySettings({
            nonPersonalizedAds: !canShowPersonalizedAds,
        });
    });
}
export async function setPubAdsRestrictedDataProcessing() {
    const isUserInUS = getState(State.isUserInUS);
    if (!isUserInUS) {
        return;
    }
    await waitForIsOptedOutOfTargeting();
    const isOptedOutOfTargeting = getState(State.isOptedOutOfTargeting);
    googletag.cmd.push(() => {
        googletag.pubads().setPrivacySettings({
            restrictDataProcessing: isOptedOutOfTargeting,
        });
    });
}
export function slotVisibilityChangeHandler(event, slotDataArray) {
    const slotData = find(slotDataArray, [
        'mintSlot.elementId',
        event.slot.getSlotElementId(),
    ]);
    const isEventFromCurrentAdUnit = get(slotData, 'mintSlot.formatDefinition.adUnitId') ===
        event.slot.getAdUnitPath();
    if (isEventFromCurrentAdUnit) {
        if (get(slotData, 'shouldIgnoreAdVisibilityEventOnce')) {
            slotData.shouldIgnoreAdVisibilityEventOnce = false;
            return;
        }
        const isSlotTimerPaused = get(slotData, 'timer.isPaused');
        const inViewPercentage = event.inViewPercentage;
        const slotWidth = get(slotData, 'mintSlot.currentDimensions[0]');
        const slotHeight = get(slotData, 'mintSlot.currentDimensions[1]');
        const adSizeString = `view.${slotWidth}x${slotHeight}`;
        if (inViewPercentage === 0 && !isSlotTimerPaused) {
            mediaEvents
                .event(Events.Media.display, Events.Status.paused)
                .emit(adSizeString);
            slotData.timer.pause();
        }
        if (inViewPercentage !== 0 && isSlotTimerPaused) {
            mediaEvents
                .event(Events.Media.display, Events.Status.resumed)
                .emit(adSizeString);
            slotData.timer.resume();
        }
    }
}
export function addSlotVisibilityChangeListener(slotDataArray) {
    googletag.pubads().addEventListener('slotVisibilityChanged', (event) => {
        slotVisibilityChangeHandler(event, slotDataArray);
    });
}
